import {
  requestFlow,
  requestMultipleOutcomesType,
  RequestResponse,
  requestStatus,
  RequestStatus,
} from 'src/lib/services/api/request-api';
import { totalLengthToString } from 'src/features/requests/request.helper';
import { ExtraSubtitlesOptions } from 'src/helpers/request_types';

const decorateRequest = (request?: RequestResponse) => {
  if (!request) {
    return request;
  }

  const isOfStatus = (status: RequestStatus | RequestStatus[]): boolean => {
    if (Array.isArray(status)) {
      return status.includes(request?.status as any);
    }

    return status === request?.status;
  };

  const hasRevisions = !!request.revisions_count,
    revisionsCount = request.revisions_count ?? 0,
    latestRevision = [...request.revisions].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )?.[0],
    isQueued = isOfStatus(requestStatus.queued),
    isCurrentlyEditing = isOfStatus(requestStatus.currentlyEditing),
    isDelivered = isOfStatus(requestStatus.delivered),
    needsReedits = !!request?.requested_reedit_at,
    // `requested_reedit_at` persists across the queued→editing→delivered
    // cycle so it's still set on a post-re-edit Ready-for-Review row. On
    // that pass the editor has already addressed the request and the
    // customer should see Re-edit + Approve, not "Undo re-edit request".
    // Compare against the latest revision: a revision newer than the
    // re-edit timestamp means the editor's response is in (#2383).
    hasUnresolvedReedit =
      needsReedits &&
      (!latestRevision ||
        new Date(latestRevision.created_at) <= new Date(request.requested_reedit_at!)),
    needsResizes = !!request?.resolutions?.resize?.length,
    needsCaptionsFile = request?.extra?.subtitles === ExtraSubtitlesOptions.separateSrt,
    isApproved = !!request?.approved_at,
    isCompleted = isOfStatus(requestStatus.complete),
    media = request.media?.footage ?? [],
    hasMedia = !!media.length,
    isDefaultFlow = request.flow === requestFlow.default,
    isFolder = [requestFlow.multiOutcome, requestFlow.repurpose].includes(request.flow as any),
    hasEditor = !!request.editor,
    editor = request.editor,
    needsAttention = !!request.requested_attention_at,
    // Multi-outcome attention lives on the individual outcome, not the parent
    // project — for those, parent.requested_attention_at (needsAttention) stays
    // null even when the editor flagged one outcome. Mirror the submit-button
    // rule so the Edit button (gated on canEdit) doesn't disappear on a
    // multi-outcome project in Needs Attention state (#2504 review).
    hasOutcomeAttention =
      request.multiple_outcomes?.outcomes?.some((outcome) => !!outcome.requested_attention_at) ??
      false,
    canEdit =
      needsAttention ||
      hasOutcomeAttention ||
      isOfStatus([requestStatus.draft, requestStatus.queued]),
    totalLength = totalLengthToString(request.total_length) || request.description?.total_length,
    canRateRevision = !request?.rated_revisions_count,
    canRequestReEdit = isDelivered && !hasUnresolvedReedit,
    needsApproval =
      (needsResizes && !latestRevision?.media?.some((m) => m.collection_name === 'resizes')) ||
      (needsCaptionsFile && !latestRevision?.media?.some((m) => m.collection_name === 'captions')),
    // `canApprove` and `canComplete` must stay mutually exclusive: both render a
    // button carrying the same `request-approve-button` id, which UserGuiding's
    // onboarding tour anchors a tooltip to (see reedit-and-approve-buttons.tsx).
    // Today they are — `canApprove` needs `needsApproval && !isApproved`, and
    // with `needsApproval` true `canComplete` needs `isApproved`. If a change
    // here lets both be true at once, two nodes share that id and the tooltip
    // attaches to whichever the DOM yields first.
    canApprove = isDelivered && !isApproved && !hasUnresolvedReedit && needsApproval,
    canComplete =
      isDelivered && !hasUnresolvedReedit && (!needsApproval || (isApproved && !isDefaultFlow));

  return {
    ...request,
    isQueued,
    isCurrentlyEditing,
    isDelivered,
    needsReedits,
    hasUnresolvedReedit,
    isApproved,
    isCompleted,
    hasRevisions,
    revisionsCount,
    media,
    hasMedia,
    isDefaultFlow,
    isFolder,
    hasEditor,
    editor,
    needsAttention,
    canEdit,
    canRateRevision,
    canRequestReEdit,
    canApprove,
    canComplete,
    totalLength,
    isOfStatus,
    // Project strategy (mirrors admin): 'editor_choice' = "Editor's selection",
    // 'custom' = "Customized". Read from multiple_outcomes.outcome_type.
    isEditorsChoice:
      request.multiple_outcomes?.outcome_type === requestMultipleOutcomesType.editorChoice,
    isCustom: request.multiple_outcomes?.outcome_type === requestMultipleOutcomesType.custom,
  };
};

export type DecoratedRequest = ReturnType<typeof decorateRequest>;
export { decorateRequest };
